import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import axios from '../api/axios';
import { BACKEND_URL } from '../config';
import './Stats.css';

const socket = io(BACKEND_URL);

function Stats() {
  const [stats, setStats] = useState({
    'twitch-main': [],
    'twitch-247': [],
    'youtube': [],
    'discord': []
  });
  const [selectedPlatform, setSelectedPlatform] = useState('twitch-main');
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [jobSearch, setJobSearch] = useState('');

  useEffect(() => {
    loadStats();
    loadJobs();
    socket.on('jobs:updated', loadJobs);
    return () => socket.off('jobs:updated', loadJobs);
  }, []);

  const loadJobs = async () => {
    try {
      const response = await axios.get('/api/stats/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const deleteJob = async (username) => {
    try {
      await axios.delete(`/api/stats/jobs/${username}`);
      setJobs(prev => prev.filter(j => j.username !== username));
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get('/api/stats/users');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const platformUsers = stats[selectedPlatform] || [];
  const filteredJobs = jobs.filter(j =>
    j.username.toLowerCase().includes(jobSearch.toLowerCase()) ||
    j.job.toLowerCase().includes(jobSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="stats-page">
        <div className="page-header">
          <h2>📊 User Statistics</h2>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Lade Statistiken...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-page">
      <div className="page-header">
        <h2>📊 User Statistics</h2>
        <p>Punkte und Watchtime aller User</p>
      </div>

      <div className="platform-tabs">
        <button
          className={activeTab === 'stats' && selectedPlatform === 'twitch-main' ? 'active' : ''}
          onClick={() => { setActiveTab('stats'); setSelectedPlatform('twitch-main'); }}
        >
          💜 Twitch Main
        </button>
        <button
          className={activeTab === 'stats' && selectedPlatform === 'twitch-247' ? 'active' : ''}
          onClick={() => { setActiveTab('stats'); setSelectedPlatform('twitch-247'); }}
        >
          🔄 Twitch 24/7
        </button>
        <button
          className={activeTab === 'stats' && selectedPlatform === 'youtube' ? 'active' : ''}
          onClick={() => { setActiveTab('stats'); setSelectedPlatform('youtube'); }}
        >
          ▶️ YouTube
        </button>
        <button
          className={activeTab === 'stats' && selectedPlatform === 'discord' ? 'active' : ''}
          onClick={() => { setActiveTab('stats'); setSelectedPlatform('discord'); }}
        >
          💙 Discord
        </button>
        <button
          className={activeTab === 'jobs' ? 'active' : ''}
          onClick={() => setActiveTab('jobs')}
        >
          💼 Jobs {jobs.length > 0 && <span className="tab-badge">{jobs.length}</span>}
        </button>
      </div>

      {activeTab === 'jobs' ? (
        <div className="job-list-section">
          <div className="job-list-header">
            <div>
              <h3>💼 Zuschauer Jobs</h3>
              <p className="job-participation">
                An dieser Umfrage haben bereits <strong>{jobs.length}</strong> {jobs.length === 1 ? 'Nutzer' : 'Nutzer'} teilgenommen.
              </p>
              <p className="job-hint">Zuschauer können mit <code>!job &lt;Beruf&gt;</code> ihren Job eintragen.</p>
            </div>
            <input
              className="job-search"
              type="text"
              placeholder="Suchen..."
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
            />
          </div>
          <div className="job-table-container">
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Job</th>
                  <th>Eingetragen</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#adadb8' }}>
                      {jobs.length === 0 ? 'Noch keine Jobs eingetragen' : 'Keine Ergebnisse'}
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map((entry, index) => (
                    <tr key={entry.username}>
                      <td className="rank-cell"><span className="rank-number">{index + 1}</span></td>
                      <td className="username-cell">{entry.username}</td>
                      <td className="job-cell">{entry.job}</td>
                      <td className="lastseen-cell">
                        {new Date(entry.updated_at).toLocaleString('de-DE')}
                      </td>
                      <td>
                        <button
                          className="job-delete-btn"
                          onClick={() => deleteJob(entry.username)}
                          title="Eintrag löschen"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="stats-summary">
            <div className="summary-card">
              <h3>Total Users</h3>
              <div className="stat-big">{platformUsers.length}</div>
            </div>
            <div className="summary-card">
              <h3>Total Points</h3>
              <div className="stat-big">
                {platformUsers.reduce((sum, user) => sum + user.points, 0).toLocaleString()}
              </div>
            </div>
            <div className="summary-card">
              <h3>Total Watchtime</h3>
              <div className="stat-big">
                {formatTime(platformUsers.reduce((sum, user) => sum + user.watchtime_minutes, 0))}
              </div>
            </div>
          </div>

          <div className="stats-table-container">
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Points</th>
                  <th>Watchtime</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {platformUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#adadb8' }}>
                      Keine User-Daten vorhanden
                    </td>
                  </tr>
                ) : (
                  platformUsers
                    .sort((a, b) => b.points - a.points)
                    .map((user, index) => (
                      <tr key={user.user_id}>
                        <td className="rank-cell">
                          {index + 1 <= 3 ? (
                            <span className={`rank-badge rank-${index + 1}`}>
                              {index + 1 === 1 ? '🥇' : index + 1 === 2 ? '🥈' : '🥉'}
                            </span>
                          ) : (
                            <span className="rank-number">{index + 1}</span>
                          )}
                        </td>
                        <td className="username-cell">{user.username}</td>
                        <td className="points-cell">{user.points.toLocaleString()}</td>
                        <td className="watchtime-cell">{formatTime(user.watchtime_minutes)}</td>
                        <td className="lastseen-cell">
                          {new Date(user.last_seen).toLocaleString('de-DE')}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Stats;
